import * as React from "react";
import { useState, useEffect } from "react";
import Input from "@/components/atomics/Input";
import TextArea from "@/components/atomics/text-area";
import StepLoad from "@/components/atomics/step-load";
import Direction from "@/components/atomics/direction";
import MetaData from "@/components/atomics/meta-data";
import { useNavigate } from "react-router-dom";
import { useCollection } from "@/context/CollectionContext";
import { BACKEND_URL } from "@/config";
import {
  CollectionDataCache,
  CollectionDataUrls,
  CollectionMintEntry,
} from "@/types";
import {
  CollectionMintData,
  useMasterContract,
} from "@/hooks/useMasterContract";
import { useTonConnect } from "@/hooks/useTonConnect";
import Loading from "@/components/atomics/loading";
import Help from "@/components/atomics/help";
import ThirdStepHelp from "@/components/atomics/third-step-help";
import CollectionSuccessMessage from "@/components/atomics/collection-success-message";
import CollectionErrorMessage from "@/components/atomics/collection-error-message";
import { helpDescription } from "@/data/help";
import MotionModal from "@/components/atomics/motion-modal";
import { backend } from "@/services/backend";
import { useSnackbar } from "notistack";
import { Attribute } from "@/types";
import Agreement from "@/components/atomics/agreement";
import LoadingStatus from "@/components/atomics/loading-status";
import { loadingData } from "@/data/loading-data";
import { creationTonPrice } from "@/config";
import { RootState } from "@/state/reducer";
import { useSelector } from "react-redux";
import { ratingBackend } from "@/services/ratingBackend";

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export default function ThirdStep() {
  const navigate = useNavigate();

  const snackbar = useSnackbar();
  const {
    ipfsUrls,
    formData: secondStepFormData,
    collectionIcon,
    initialize,
  } = useCollection();
  const [collectionImage, setCollectionImage] = useState<string | null>(
    collectionIcon || null
  );
  const masterContract = useMasterContract();
  const { wallet, fetchBalance } = useTonConnect();
  const [isLoading, setIsLoading] = useState(false);
  const [isMinting, setIsMinting] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [helpTitle, setHelpTitle] = useState("");
  const [collectionAddress, setCollectionAddress] = useState<string | null>(
    null
  );
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [newCollectionData, setNewCollectionData] =
    useState<CollectionDataCache>();
  const balance = useSelector((state: RootState) => state.balance);
  const [previousExperience, setPreviousExperience] = useState<number>(0);

  useEffect(() => {
    const areIpfsUrlsValid = ipfsUrls.cover && ipfsUrls.icon && ipfsUrls.nft;
    if (!areIpfsUrlsValid) {
      navigate("/collection/create/1");
    }
  }, []);

  const mintAction = async (collectionUrl: string, itemUrl: string) => {
    try {
      if (!wallet) {
        snackbar.enqueueSnackbar("Please connect your wallet first", {
          SnackbarProps: {
            onClick: () => {
              snackbar.closeSnackbar();
            },
          },
          variant: "error",
          autoHideDuration: 1000,
        });
        return;
      }

      // Fetch previous experience before collection creation
      if (wallet) {
        try {
          const ownerData = await ratingBackend.getOwnerRatingData(wallet);
          if (ownerData && ownerData.experience !== undefined) {
            setPreviousExperience(Number(ownerData.experience));
          }
        } catch (error) {
          console.error("Error fetching previous experience:", error);
          // Continue with collection creation even if previous experience fetch fails
        }
      }

      // Fetch fresh balance before checking
      // const currentBalance = await fetchBalance();
      /* if (!currentBalance || Number(currentBalance) < Number(creationTonPrice)) {
                snackbar.enqueueSnackbar("Insufficient balance. Creation fee is 0.25 TON", { variant: "error", autoHideDuration: 1000 });
                return;
            } */

      const royaltyPercent = Number(secondStepFormData["ROYALTY_%"]) || 0;
      const normalizedRoyalty = Math.min(Math.max(royaltyPercent, 0), 1000);

      const dataToSend: CollectionMintData = {
        collectionDataUrl: collectionUrl,
        itemDataUrl: itemUrl,
        item_price: Number(secondStepFormData["PRICE IN TON"]),
        mint_limit: secondStepFormData["MINT LIMIT_ENABLED"]
          ? Number(secondStepFormData["MINT LIMIT"])
          : 0,
        is_sbt: !!secondStepFormData["SBT"],
        mint_time_limit: secondStepFormData["TIME LIMIT_ENABLED"]
          ? Math.round(
              new Date(String(secondStepFormData["TIME LIMIT"])).getTime() /
                1000
            )
          : 0,
        owner_address: wallet,
        royaltyParams: {
          numenator: normalizedRoyalty * 10,
          denumenator: 1000,
          destination: wallet,
        },
        enable_whitelist: !!secondStepFormData["enable_whitelist"],
        user_item_limit: secondStepFormData["enable_mintlimit_perWallet"]
          ? BigInt(1)
          : BigInt(0),
      };

      interface MintResult {
        success: boolean;
        address?: string;
        transaction?: any;
      }
      const result = (await masterContract.mintCollection(
        dataToSend
      )) as MintResult;
      console.log("Received collection data:", result);
      setCollectionAddress(result.address || null);
      setIsMinting(true);

      if (result.success && result.address) {
        const maxAttempts = 24;
        let attempts = 0;

        while (attempts < maxAttempts) {
          const isDeployed = await backend.getDeployStatus(result.address);

          if (isDeployed) {
            // Upload fresh collection data at first
            const newCollectionData = await backend.getCollectionData(
              result.address
            );
            if (newCollectionData) {
              setNewCollectionData(newCollectionData);
              setShowSuccessMessage(true);
            }
            initialize();
            return;
          }

          await sleep(5000);
          attempts++;
        }

        setShowErrorMessage(true);
        setErrorMessage("Collection deployment timed out after 2 minutes");
      } else {
        setShowErrorMessage(true);
        setErrorMessage(result.transaction.error);
        throw new Error("Transaction failed");
      }
    } catch (e) {
      console.error("Minting error:", e);
      snackbar.enqueueSnackbar(e.message, {
        SnackbarProps: {
          onClick: () => {
            snackbar.closeSnackbar();
          },
        },
        variant: "error",
        autoHideDuration: 1000,
      });
    } finally {
      setIsMinting(false);
    }
  };

  const saveCollectionEntry = async () => {
    setIsLoading(true);
    try {
      const collectionMintData: CollectionMintEntry = {
        collection: {
          name: formData.collectionName,
          description: formData.description,
          imageUrl: ipfsUrls.icon || "",
          backgroundUrl: ipfsUrls.cover || "",
          social_links: [],
        },
        item: {
          name: formData.nftName || formData.collectionName,
          description: formData.nftDescription || formData.description,
          image: ipfsUrls.nft || "",
          attributes: formData.metadata.map(({ key, value }) => ({
            trait_type: key,
            value: value,
          })) as Attribute[],
          content_type: ipfsUrls.nftMimeType || undefined,
          content_url: ipfsUrls.content || undefined,
        },
      };
      console.log("Mint data:", collectionMintData);
      const response = await fetch(`${BACKEND_URL}files/loadcollection`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          entry: collectionMintData,
        }),
      });
      const result: CollectionDataUrls = await response.json();
      await mintAction(result.collectionFileUrl, result.itemFileUrl);
    } catch (error) {
      alert("Error saving collection: " + error);
    } finally {
      setIsLoading(false);
    }
  };

  const [formData, setFormData] = useState({
    collectionName: "",
    description: "",
    nftName: "",
    nftDescription: "",
    metadata: [{ key: "", value: "" }],
    isAgreed: false,
  });

  const handleChange = (field: string, value: string) => {
    if (field === "collectionName" && value.length > 27) {
      snackbar.enqueueSnackbar("Collection name cannot exceed 27 characters", {
        SnackbarProps: {
          onClick: () => {
            snackbar.closeSnackbar();
          },
        },
        variant: "error",
        autoHideDuration: 2000,
      });
      return;
    }
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddPair = () => {
    const lastPair = formData.metadata[formData.metadata.length - 1];
    if (!lastPair.key.trim() || !lastPair.value.trim()) {
      return;
    }

    setFormData(prev => ({
      ...prev,
      metadata: [...prev.metadata, { key: "", value: "" }],
    }));
  };

  const handleRemovePair = (index: number) => {
    if (formData.metadata.length === 1) {
      setFormData(prev => ({
        ...prev,
        metadata: [{ key: "", value: "" }],
      }));
      return;
    }
    setFormData(prev => ({
      ...prev,
      metadata: prev.metadata.filter((_, i) => i !== index),
    }));
  };

  const handleUpdatePair = (
    index: number,
    field: "key" | "value",
    value: string
  ) => {
    setFormData(prev => {
      const newMetadata = [...prev.metadata];
      newMetadata[index][field] = value;
      return {
        ...prev,
        metadata: newMetadata,
      };
    });
  };

  const isFormValid = () => {
    return (
      formData.collectionName.trim() !== "" &&
      formData.description.trim() !== "" &&
      formData.isAgreed
    );
  };

  return (
    <>
      {isMinting && (
        <LoadingStatus
          status={loadingData[1].status}
          text={loadingData[1].text}
        />
      )}
      <div
        className={`${isMinting ? "opacity-10 pointer-events-none" : "pointer-events-auto w-full"} flex flex-col justify-center items-center pt-[20px] pb-[70px] relative`}
      >
        <MotionModal isOpen={showHelp}>
          {helpTitle === "STEP 3" ? (
            <ThirdStepHelp
              className="w-full bg-black"
              onClose={() => setShowHelp(false)}
              onOk={() => setShowHelp(false)}
            />
          ) : (
            <Help
              className="w-full bg-black"
              onClose={() => setShowHelp(false)}
              onOk={() => setShowHelp(false)}
              title={helpTitle}
              description={helpDescription[helpTitle]}
            />
          )}
        </MotionModal>
        <MotionModal isOpen={showSuccessMessage}>
          <CollectionSuccessMessage
            className="w-full bg-black"
            collectionImage={collectionImage || ""}
            collectionName={formData.collectionName}
            collectionData={newCollectionData}
            previousExperience={previousExperience}
            onClose={() => {
              setShowSuccessMessage(false);
              navigate("/");
            }}
            onClick={() => {
              navigate(`/collection/${collectionAddress}`);
            }}
          />
        </MotionModal>
        <MotionModal isOpen={showErrorMessage}>
          <CollectionErrorMessage
            className="w-full bg-black"
            errorMessage={errorMessage}
            onClose={() => setShowErrorMessage(false)}
            onClick={() => {
              setShowErrorMessage(false);
            }}
          />
        </MotionModal>
        <header className="flex flex-col justify-center text-[32px] text-center">
          <div className="relative">
            <span className={`${isLoading ? "text-[#2B2B2B]" : "text-white"}`}>
              STEP_3
            </span>
            <div
              onClick={() => {
                setShowHelp(true);
                setHelpTitle("STEP 3");
              }}
              className={`${isLoading ? "text-[#2B2B2B]" : "text-[#0099FE] hover:cursor-pointer"} absolute top-[50%] left-[calc(50%+85px)] translate-x-[-50%] translate-y-[-50%] hover:cursor-pointer `}
            >
              <span>?</span>
            </div>
          </div>
          <span className={`${isLoading ? "text-[#2B2B2B]" : "text-white"}`}>
            CUSTOMIZE
          </span>
        </header>
        <div className="w-full flex flex-col gap-[20px] pt-[30px] pb-[22px] pl-[30px] pr-[30px]">
          <Input
            label="Collection Name"
            placeholder="Simple collection"
            essential={true}
            type="text"
            isLoading={isLoading}
            value={formData.collectionName}
            onChange={e => handleChange("collectionName", e.target.value)}
            onHelpClick={() => {
              setShowHelp(true);
              setHelpTitle("COLLECTION NAME");
            }}
          />
          <TextArea
            label="Description"
            placeholder="Simple description"
            essential={true}
            totalTextCount={700}
            isLoading={isLoading}
            value={formData.description}
            onChange={e => handleChange("description", e.target.value)}
            onHelpClick={() => {
              setShowHelp(true);
              setHelpTitle("DESCRIPTION");
            }}
          />
          <Input
            label="NFT Name"
            placeholder="Simple collection"
            type="text"
            isLoading={isLoading}
            value={formData.nftName}
            onChange={e => handleChange("nftName", e.target.value)}
            onHelpClick={() => {
              setShowHelp(true);
              setHelpTitle("NFT NAME");
            }}
          />
          <TextArea
            label="NFT Description"
            placeholder="Simple description"
            totalTextCount={700}
            isLoading={isLoading}
            value={formData.nftDescription}
            onChange={e => handleChange("nftDescription", e.target.value)}
            onHelpClick={() => {
              setShowHelp(true);
              setHelpTitle("NFT DESCRIPTION");
            }}
          />
          <MetaData
            label="Metadata"
            pairs={formData.metadata}
            onAddPair={handleAddPair}
            isLoading={isLoading}
            onRemovePair={handleRemovePair}
            onUpdatePair={handleUpdatePair}
            onHelpClick={() => {
              setShowHelp(true);
              setHelpTitle("METADATA");
            }}
          />
          <Agreement
            className="flex flex-col justify-center items-center w-full pb-[40px]"
            onClick={() => {
              setFormData(prev => ({ ...prev, isAgreed: !prev.isAgreed }));
            }}
            isLoading={isLoading}
            isAgreed={formData.isAgreed}
          />
        </div>

        <div className="w-full relative flex flex-col justify-center items-center mt-[14px]">
          {!isLoading && <StepLoad currentIndex={2} totalIndex={3} />}
          <span
            className={`text-[32px] pt-[10px] ${isFormValid() || isLoading ? "text-[#0099FE] hover:cursor-pointer" : "text-[#2B2B2B] "}`}
            onClick={() => isFormValid() && saveCollectionEntry()}
          >
            {isLoading ? "CREATING" : "CREATE"}
          </span>

          {!isLoading && (
            <div
              className="absolute top-[30px] left-[60px] rotate-[180deg] text-[#0099FE] w-[17px] h-[29px]"
              onClick={() => {
                navigate("/collection/create/2");
              }}
            >
              <Direction className="w-full h-full" color="#0099FE" />
            </div>
          )}

          <Loading isLoading={isLoading} />
        </div>
      </div>
    </>
  );
}
